import { supabase } from '../lib/supabase';

/**
 * Centralized Error Logging Service
 * Captures full Supabase error objects, stack traces, and request payloads to database
 */

/**
 * Log error to database with full context
 * @param {Object} errorData - Error data to log
 * @returns {Object} Result with data or error
 */
export const logError = async (errorData) => {
  try {
    const {
      error,
      errorSource = 'unknown',
      errorSeverity = 'medium',
      requestPayload = null,
      requestMethod = null,
      requestUrl = null,
      userId = null,
      userEmail = null,
      applicationId = null,
      dealershipId = null,
      componentName = null,
      functionName = null,
      filePath = null,
      lineNumber = null,
      additionalMetadata = null
    } = errorData;

    // Extract error details
    const errorMessage = error?.message || error?.toString() || 'Unknown error';
    const errorCode = error?.code || error?.status || null;
    const errorDetails = error?.details || null;
    const errorHint = error?.hint || null;
    
    // Capture stack trace
    const stackTrace = error?.stack || new Error()?.stack || null;
    
    // Capture full error object as JSONB
    const fullErrorObject = {
      message: errorMessage,
      code: errorCode,
      details: errorDetails,
      hint: errorHint,
      name: error?.name,
      statusCode: error?.statusCode,
      ...error
    };

    // Capture browser info
    const browserInfo = {
      userAgent: navigator?.userAgent,
      platform: navigator?.platform,
      language: navigator?.language,
      screenResolution: `${window?.screen?.width}x${window?.screen?.height}`,
      viewport: `${window?.innerWidth}x${window?.innerHeight}`,
      timestamp: new Date()?.toISOString()
    };

    // Build session context
    const sessionContext = {
      url: window?.location?.href,
      pathname: window?.location?.pathname,
      referrer: document?.referrer,
      timestamp: new Date()?.toISOString()
    };

    const { data, error: logError } = await supabase
      ?.from('error_logs')
      ?.insert({
        error_source: errorSource,
        error_severity: errorSeverity,
        error_message: errorMessage,
        error_code: errorCode,
        error_details: errorDetails,
        error_hint: errorHint,
        stack_trace: stackTrace,
        full_error_object: fullErrorObject,
        request_payload: requestPayload,
        request_method: requestMethod,
        request_url: requestUrl,
        user_id: userId,
        user_email: userEmail,
        user_agent: navigator?.userAgent,
        ip_address: null, // Will be captured server-side if needed
        application_id: applicationId,
        dealership_id: dealershipId,
        component_name: componentName,
        function_name: functionName,
        file_path: filePath,
        line_number: lineNumber,
        browser_info: browserInfo,
        session_context: sessionContext,
        additional_metadata: additionalMetadata
      })
      ?.select()
      ?.single();

    if (logError) {
      // Fallback: Log to console if database logging fails
      console.error('[Error Logger] Failed to log error to database:', logError);
      console.error('[Error Logger] Original error:', error);
      return { error: logError };
    }

    return { data };
  } catch (exception) {
    // Fallback: Log to console if logging service itself fails
    console.error('[Error Logger] Exception in logError:', exception);
    console.error('[Error Logger] Original error data:', errorData);
    return { error: exception };
  }
};

/**
 * Log Supabase query error with full context
 * @param {Object} params - Error parameters
 */
export const logSupabaseError = async ({
  error,
  query,
  table,
  operation,
  userId = null,
  userEmail = null,
  applicationId = null,
  dealershipId = null,
  componentName = null,
  functionName = null
}) => {
  return await logError({
    error,
    errorSource: 'supabase_query',
    errorSeverity: error?.code === 'PGRST116' ? 'low' : 'high',
    requestPayload: {
      table,
      operation,
      query
    },
    userId,
    userEmail,
    applicationId,
    dealershipId,
    componentName,
    functionName,
    additionalMetadata: {
      supabase_error: true,
      table_name: table,
      operation_type: operation
    }
  });
};

/**
 * Fetch error logs with filters
 * @param {Object} filters - Filter options
 * @returns {Array} Error logs
 */
export const fetchErrorLogs = async (filters = {}) => {
  try {
    let query = supabase
      ?.from('error_logs')
      ?.select(`
        *,
        applications (id, customer_name, customer_email),
        dealerships (id, name)
      `)
      ?.order('created_at', { ascending: false });

    // Apply filters
    if (filters?.errorSource) {
      query = query?.eq('error_source', filters?.errorSource);
    }
    if (filters?.errorSeverity) {
      query = query?.eq('error_severity', filters?.errorSeverity);
    }
    if (filters?.componentName) {
      query = query?.eq('component_name', filters?.componentName);
    }
    if (filters?.resolved !== undefined) {
      query = query?.eq('resolved', filters?.resolved);
    }
    if (filters?.userEmail) {
      query = query?.eq('user_email', filters?.userEmail);
    }
    if (filters?.dateFrom) {
      query = query?.gte('created_at', filters?.dateFrom);
    }
    if (filters?.dateTo) {
      query = query?.lte('created_at', filters?.dateTo);
    }
    if (filters?.limit) {
      query = query?.limit(filters?.limit);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching error logs:', error);
      return { data: [], error };
    }

    // Convert to camelCase
    const formattedLogs = data?.map(log => ({
      id: log?.id,
      errorSource: log?.error_source,
      errorSeverity: log?.error_severity,
      errorMessage: log?.error_message,
      errorCode: log?.error_code,
      errorDetails: log?.error_details,
      errorHint: log?.error_hint,
      stackTrace: log?.stack_trace,
      fullErrorObject: log?.full_error_object,
      requestPayload: log?.request_payload,
      requestMethod: log?.request_method,
      requestUrl: log?.request_url,
      userId: log?.user_id,
      userEmail: log?.user_email,
      userAgent: log?.user_agent,
      ipAddress: log?.ip_address,
      applicationId: log?.application_id,
      applicationCustomerName: log?.applications?.customer_name || null,
      dealershipId: log?.dealership_id,
      dealershipName: log?.dealerships?.name || null,
      componentName: log?.component_name,
      functionName: log?.function_name,
      filePath: log?.file_path,
      lineNumber: log?.line_number,
      browserInfo: log?.browser_info,
      sessionContext: log?.session_context,
      additionalMetadata: log?.additional_metadata,
      resolved: log?.resolved,
      resolvedBy: log?.resolved_by,
      resolvedAt: log?.resolved_at,
      resolutionNotes: log?.resolution_notes,
      createdAt: log?.created_at
    })) || [];

    return { data: formattedLogs, error: null };
  } catch (error) {
    console.error('Exception in fetchErrorLogs:', error);
    return { data: [], error };
  }
};

/**
 * Mark error as resolved
 * @param {string} errorId - Error log ID
 * @param {Object} resolutionData - Resolution details
 */
export const resolveError = async (errorId, resolutionData) => {
  try {
    const { resolvedBy, resolutionNotes } = resolutionData;

    const { data, error } = await supabase
      ?.from('error_logs')
      ?.update({
        resolved: true,
        resolved_by: resolvedBy,
        resolved_at: new Date()?.toISOString(),
        resolution_notes: resolutionNotes
      })
      ?.eq('id', errorId)
      ?.select()
      ?.single();

    if (error) {
      console.error('Error resolving error log:', error);
      return { error };
    }

    return { data };
  } catch (error) {
    console.error('Exception in resolveError:', error);
    return { error };
  }
};

/**
 * Get error log statistics
 * @param {Object} filters - Filter options
 * @returns {Object} Statistics
 */
export const getErrorLogStats = async (filters = {}) => {
  try {
    let query = supabase?.from('error_logs')?.select('*');

    if (filters?.dateFrom) {
      query = query?.gte('created_at', filters?.dateFrom);
    }
    if (filters?.dateTo) {
      query = query?.lte('created_at', filters?.dateTo);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching error log stats:', error);
      return { data: null, error };
    }

    const stats = {
      total: data?.length || 0,
      bySeverity: {
        low: data?.filter(log => log?.error_severity === 'low')?.length || 0,
        medium: data?.filter(log => log?.error_severity === 'medium')?.length || 0,
        high: data?.filter(log => log?.error_severity === 'high')?.length || 0,
        critical: data?.filter(log => log?.error_severity === 'critical')?.length || 0
      },
      bySource: {
        supabase_query: data?.filter(log => log?.error_source === 'supabase_query')?.length || 0,
        supabase_auth: data?.filter(log => log?.error_source === 'supabase_auth')?.length || 0,
        supabase_storage: data?.filter(log => log?.error_source === 'supabase_storage')?.length || 0,
        api_call: data?.filter(log => log?.error_source === 'api_call')?.length || 0,
        client_error: data?.filter(log => log?.error_source === 'client_error')?.length || 0
      },
      resolved: data?.filter(log => log?.resolved)?.length || 0,
      unresolved: data?.filter(log => !log?.resolved)?.length || 0
    };

    return { data: stats, error: null };
  } catch (error) {
    console.error('Exception in getErrorLogStats:', error);
    return { data: null, error };
  }
};